import { useStore } from 'vuex'
import type { Store } from 'vuex'
import type { RootState } from '@/store'
import { NAMESPACES } from '@/store/types'
import { LOCATION_GETTERS, LOCATION_ACTIONS, LocationError } from '@/store/location'
import type { LocationCoords } from '@/store/location'
import { MAP_ACTIONS } from '@/store/map'

const locationStore = `${NAMESPACES.USER}/${NAMESPACES.LOCATION}`

export const locationCoordsRegex = {
  latitude: /^[-+]?([1-8]?\d(\.\d{1,8})?|90(\.0{1,8})?)$/,
  longitude: /^[+-]?(((1[0-7]\d)|([1-9]?\d))(\.\d{1,8})?|180(\.0{1,8})?)$/
} as const

function scrapeErrors(coords: LocationCoords): LocationCoords | false {
  if (coords && !coords.error) {
    return coords
  }
  return false
}

function getGeolocation(
  store: Store<RootState>,
  callback: (result: LocationCoords | false) => void
): void {
  const currentLocation = store.getters[
    `${locationStore}/${LOCATION_GETTERS.CURRENT_LOCATION}`
  ] as LocationCoords

  if (currentLocation.error) {
    if (currentLocation.error == LocationError.DISALLOWED) {
      callback(false)
      return
    }

    if (currentLocation.error == LocationError.UNKNOWN) {
      store.watch(
        (state: RootState) => state.user.location.loading,
        (loading: boolean) => {
          if (!loading) {
            callback(
              scrapeErrors(store.getters[`${locationStore}/${LOCATION_GETTERS.CURRENT_LOCATION}`])
            )
          }
        }
      )

      store.dispatch(`${locationStore}/${LOCATION_ACTIONS.UPDATE_LOCATION}`)
    }
  } else {
    callback(currentLocation)
  }
}

function getLocation(store: Store<RootState>, callback: (coords: LocationCoords) => void): unknown {
  const savedLoc = localStorage.getItem('tsi.mapLocation')
  const coords = store.state.map.location.center
  return savedLoc
    ? callback(coords)
    : getGeolocation(store, (result) => {
        if (result !== false) callback(result)
      })
}

function setLocation(store: Store<RootState>, coords: LocationCoords): void {
  store.dispatch(`${NAMESPACES.MAP}/${MAP_ACTIONS.SET_CENTER}`, coords)
}

export function useLocationGetter() {
  const store = useStore<RootState>()
  return {
    getGeolocation: (callback: (result: LocationCoords | false) => void) =>
      getGeolocation(store, callback),
    getLocation: (callback: (coords: LocationCoords) => void) => getLocation(store, callback),
    setLocation: (coords: LocationCoords) => setLocation(store, coords)
  }
}
